import { apiService } from './api';
import apiClient from './api';
import { ApiResponse } from '../types';
import { unwrapResponse } from './apiUtils';
import {
  WorkflowStatus,
  BatchWorkflow,
  BatchWorkflowListRequest,
  BatchWorkflowListResponse,
  CreateBatchWorkflowDto,
  UpdateBatchWorkflowDto,
  WorkflowTransitionLog,
  WorkflowDashboardData,
  BulkStatusUpdateResponse,
  LinkBatchToWorkflowRequest,
  WorkflowDocument,
  RelatedWorkflowDocuments,
  SageDocumentLink,
  CreateSageDocumentLinkDto,
  UpdateSageDocumentLinkStatusDto,
  LabIntake,
  UpdateDeliveryInfoDto,
  SaveSampleIntakeLinesDto,
  WorkflowSampleFile,
  WorkflowSampleFiles,
  UpdateStatusRequest,
  TriggerTransitionRequest,
  BulkStatusUpdateRequest,
  SendReportsRequest,
  SendReportsResult,
  EntitySearchResult,
  WorkflowComment,
  LabTriggeredCreateResult,
  OpenWorkflowSummary,
  ReportEmailPreviewData,
} from '../types/workflow';
import type { WorkflowHistoryItem, ActivityPriority } from '../types/workflow/activity';
import type { WorkflowReviewApprovalState, WorkflowApprovalType } from '../types/workflow/reviewApproval';
import type { ResponsibleUser } from '../types/workflow/statusRole';
import type { ReportType, ReportPdfStatus, BatchTypePdfStatus } from '../components/Workflow/WorkflowDetailPage/sections/sendReportTypes';
import type { WorkflowFileValidationResult, ValidationIssue, ValidationSeverity } from '../types/workflow/validation';
import { mapSeverityFromApi } from '../types/workflow/validation';

// ==========================================================================
// API RESPONSE TYPES
// These types represent the raw API response before transformation.
// The backend sends severity as numeric values (0=Error, 1=Warning, 2=Info).
// ==========================================================================

/**
 * Raw validation issue from API with numeric severity.
 * Maps to ValidationIssueDto in backend.
 */
interface ApiValidationIssue {
  severity: number | ValidationSeverity; // Backend may send number or string
  code: string;
  message: string;
  fileType?: string;
  rowNumber?: number;
  context: Record<string, string>;
}

/**
 * Raw file validation result from API with numeric severity issues.
 * Maps to FileValidationResultDto in backend.
 */
interface ApiFileValidationResult {
  fileType: string;
  fileName: string;
  isValid: boolean;
  hasWarnings: boolean;
  expectedCount: number;
  actualCount: number;
  countMatches: boolean;
  landRefMatches: Array<{
    fileLandRef: string;
    intakeLandBlock?: string;
    intakeLineNumber?: number;
    isMatched: boolean;
    fileRowCount: number;
  }>;
  unmatchedFileLandRefs: string[];
  unmatchedIntakeLandBlocks: string[];
  duplicates: Array<{
    sampleNumber: string;
    rowNumbers: number[];
  }>;
  issues: ApiValidationIssue[];
  samplePreviews: Array<{
    rowNumber: number;
    sampleNumber: string;
    landRef?: string;
    clientRef?: string;
    dateReceived?: string;
  }>;
}

/**
 * Raw workflow validation result from API with numeric severity values.
 * Maps to WorkflowFileValidationResultDto in backend.
 */
interface ApiValidationResult {
  workflowId: number;
  isValid: boolean;
  hasWarnings: boolean;
  validatedAt: string;
  sapValidation?: ApiFileValidationResult;
  tissueValidation?: ApiFileValidationResult;
  soilValidation?: ApiFileValidationResult;
  crossFileIssues: ApiValidationIssue[];
  totalErrors: number;
  totalWarnings: number;
}


/**
 * Transforms validation issues from API response, converting numeric severity to string.
 * @param issues Raw issues from API with numeric severity
 * @returns Transformed issues with string severity type
 */
function transformValidationIssues(issues: ApiValidationIssue[]): ValidationIssue[] {
  return issues.map((issue) => ({
    ...issue,
    severity: typeof issue.severity === 'number' ? mapSeverityFromApi(issue.severity) : issue.severity,
  }));
}

/**
 * Transforms a file validation result from API response.
 * @param fileResult Raw file validation result from API
 * @returns Transformed result with string severity types, or undefined if input is falsy
 */
function transformFileValidationResult(fileResult: ApiFileValidationResult | undefined) {
  if (!fileResult) return undefined;
  return {
    ...fileResult,
    issues: transformValidationIssues(fileResult.issues || []),
  };
}

/**
 * Transforms the full workflow validation result from API response.
 * Converts numeric severity values to string literal types.
 * @param result Raw validation result from API
 * @returns Transformed result matching WorkflowFileValidationResult interface
 */
function transformValidationResult(result: ApiValidationResult): WorkflowFileValidationResult {
  return {
    ...result,
    sapValidation: transformFileValidationResult(result.sapValidation),
    tissueValidation: transformFileValidationResult(result.tissueValidation),
    soilValidation: transformFileValidationResult(result.soilValidation),
    crossFileIssues: transformValidationIssues(result.crossFileIssues || []),
  };
}

/**
 * Service for managing batch workflows, statuses, and transitions.
 */
export const workflowService = {
  // ==========================================================================
  // WORKFLOW STATUSES
  // ==========================================================================

  /**
   * Get a specific workflow status by its code.
   * @param code The status code (e.g., "NEW", "APPROVED")
   * @param signal Optional AbortSignal for request cancellation
   */
  getStatusByCode: async (code: string, signal?: AbortSignal): Promise<WorkflowStatus> => {
    const response = await apiService.get<WorkflowStatus>(`/workflow/statuses/${encodeURIComponent(code)}`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch workflow status');
  },

  // ==========================================================================
  // DUPLICATE CHECK
  // ==========================================================================

  /**
   * Check for open workflows matching the given farmer+farm criteria.
   * Used to warn about potential duplicates before workflow creation.
   * @param farmerId Optional farmer ID
   * @param farmId Optional farm ID
   */
  checkDuplicates: async (
    farmerId?: number | null,
    farmId?: number | null,
  ): Promise<OpenWorkflowSummary[]> => {
    const params = new URLSearchParams();
    if (farmerId != null) params.set('farmerId', String(farmerId));
    if (farmId != null) params.set('farmId', String(farmId));
    const queryString = params.toString();
    const url = queryString ? `/workflow/check-duplicates?${queryString}` : '/workflow/check-duplicates';
    const response = await apiService.get<OpenWorkflowSummary[]>(url);
    return unwrapResponse(response, 'Failed to check for duplicate workflows');
  },

  // ==========================================================================
  // BATCH WORKFLOW CRUD
  // ==========================================================================

  /**
   * Get workflow for a specific batch.
   * @param batchId The batch ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getByBatchId: async (batchId: number, signal?: AbortSignal): Promise<BatchWorkflow> => {
    const response = await apiService.get<BatchWorkflow>(`/workflow/batch/${batchId}`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch batch workflow');
  },

  /**
   * Create a new workflow (with optional batch link).
   * Supports both batch-linked and standalone workflows.
   * @param dto The workflow creation data (batchId is optional for standalone workflows)
   */
  create: async (dto: CreateBatchWorkflowDto): Promise<BatchWorkflow> => {
    const response = await apiService.post<BatchWorkflow>('/workflow/batch', dto);
    return unwrapResponse(response, 'Failed to create batch workflow');
  },

  /**
   * Create a lab-triggered workflow with files in a single request.
   * Atomically: creates workflow, uploads files to S3, creates staging session.
   * Uses multipart/form-data for file + DTO binding via [FromForm].
   * @param dto The workflow creation data
   * @param files The sample files to upload (at least one required)
   */
  createLabTriggered: async (
    dto: CreateBatchWorkflowDto,
    files: { sap?: File; tissue?: File; soil?: File },
    signal?: AbortSignal,
  ): Promise<LabTriggeredCreateResult> => {
    const formData = new FormData();

    // Append DTO fields as individual form fields for ASP.NET Core [FromForm] binding
    formData.append('flowType', dto.flowType);
    if (dto.priority) formData.append('priority', dto.priority);
    if (dto.clientReference) formData.append('clientReference', dto.clientReference);
    if (dto.labInstructions) formData.append('labInstructions', dto.labInstructions);
    if (dto.farmerId != null) formData.append('farmerId', String(dto.farmerId));
    if (dto.farmId != null) formData.append('farmId', String(dto.farmId));
    if (dto.deliverToLabId != null) formData.append('deliverToLabId', String(dto.deliverToLabId));
    if (dto.slaExpectedDate) formData.append('slaExpectedDate', dto.slaExpectedDate);
    if (dto.billingEntityType) formData.append('billingEntityType', dto.billingEntityType);
    if (dto.billingEntityId != null) formData.append('billingEntityId', String(dto.billingEntityId));
    if (dto.supplierEntityType) formData.append('supplierEntityType', dto.supplierEntityType);
    if (dto.supplierEntityId != null) formData.append('supplierEntityId', String(dto.supplierEntityId));
    if (dto.brandEntityType) formData.append('brandEntityType', dto.brandEntityType);
    if (dto.brandEntityId != null) formData.append('brandEntityId', String(dto.brandEntityId));
    if (dto.salesAgentEntityType) formData.append('salesAgentEntityType', dto.salesAgentEntityType);
    if (dto.salesAgentEntityId != null) formData.append('salesAgentEntityId', String(dto.salesAgentEntityId));
    // Recommendation / PDF recipients are legacy (DRI-85) and are never sent.

    // Append files
    if (files.sap) formData.append('sapFile', files.sap);
    if (files.tissue) formData.append('tissueFile', files.tissue);
    if (files.soil) formData.append('soilFile', files.soil);

    const response = await apiClient.post<ApiResponse<LabTriggeredCreateResult>>(
      '/workflow/create-lab-triggered',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, signal },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to create lab-triggered workflow');
    }
    return response.data.data;
  },

  /**
   * Get workflow by its ID (supports standalone workflows).
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getById: async (workflowId: number, signal?: AbortSignal): Promise<BatchWorkflow> => {
    const response = await apiService.get<BatchWorkflow>(`/workflow/${workflowId}`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch workflow');
  },

  /**
   * Link an existing batch to a standalone workflow.
   * @param workflowId The workflow ID to link the batch to
   * @param batchId The batch ID to link
   */
  linkBatch: async (workflowId: number, batchId: number): Promise<BatchWorkflow> => {
    const request: LinkBatchToWorkflowRequest = { batchId };
    const response = await apiService.post<BatchWorkflow>(`/workflow/${workflowId}/link-batch`, request);
    return unwrapResponse(response, 'Failed to link batch to workflow');
  },

  /**
   * Update workflow metadata by workflow ID (not status).
   * Supports both batch-linked and standalone workflows.
   * @param workflowId The workflow ID
   * @param dto The workflow update data
   */
  updateById: async (workflowId: number, dto: UpdateBatchWorkflowDto): Promise<BatchWorkflow> => {
    const response = await apiService.put<BatchWorkflow>(`/workflow/${workflowId}`, dto);
    return unwrapResponse(response, 'Failed to update workflow');
  },

  /**
   * Delete a workflow and all associated data.
   * Blocked by the backend if Sage document links exist.
   * @param workflowId The workflow ID to delete
   */
  deleteWorkflow: async (workflowId: number): Promise<boolean> => {
    const response = await apiService.delete<boolean>(`/workflow/${workflowId}`);
    return unwrapResponse(response, 'Failed to delete workflow');
  },

  // ==========================================================================
  // WORKFLOW STATUS OPERATIONS
  // ==========================================================================

  /**
   * Get all workflow statuses.
   * @param signal Optional AbortSignal for request cancellation
   */
  getStatuses: async (signal?: AbortSignal): Promise<WorkflowStatus[]> => {
    const response = await apiService.get<WorkflowStatus[]>('/workflow/statuses', undefined, signal);
    return unwrapResponse(response, 'Failed to fetch workflow statuses');
  },

  /**
   * Update workflow status.
   * @param workflowId The workflow ID
   * @param request The status update request (newStatusId and optional notes)
   */
  updateStatus: async (workflowId: number, request: UpdateStatusRequest): Promise<BatchWorkflow> => {
    const response = await apiService.put<BatchWorkflow>(`/workflow/${workflowId}/status`, request);
    return unwrapResponse(response, 'Failed to update workflow status');
  },

  /**
   * Trigger an automatic status transition.
   * Used when a workflow event (like file upload) should trigger a status change.
   * @param workflowId The workflow ID
   * @param request The trigger request (trigger name and optional notes)
   */
  triggerStatusTransition: async (workflowId: number, request: TriggerTransitionRequest): Promise<BatchWorkflow> => {
    const response = await apiService.post<BatchWorkflow>(`/workflow/${workflowId}/trigger-transition`, request);
    return unwrapResponse(response, 'Failed to trigger status transition');
  },

  /**
   * Get available transitions for a workflow.
   * Returns statuses the current user can transition to based on permissions.
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getAvailableTransitions: async (workflowId: number, signal?: AbortSignal): Promise<WorkflowStatus[]> => {
    const response = await apiService.get<WorkflowStatus[]>(
      `/workflow/${workflowId}/available-transitions`,
      undefined,
      signal
    );
    return unwrapResponse(response, 'Failed to fetch available transitions');
  },

  /**
   * Bulk update workflow status for multiple workflows.
   * @param request The bulk update request containing workflow IDs and target status
   */
  bulkUpdateStatus: async (request: BulkStatusUpdateRequest): Promise<BulkStatusUpdateResponse> => {
    const response = await apiService.post<BulkStatusUpdateResponse>('/workflow/bulk-status', request);
    return unwrapResponse(response, 'Failed to bulk update workflow status');
  },

  /**
   * Get transition history by workflow ID.
   * Supports both batch-linked and standalone workflows.
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getTransitionHistoryById: async (workflowId: number, signal?: AbortSignal): Promise<WorkflowTransitionLog[]> => {
    const response = await apiService.get<WorkflowTransitionLog[]>(`/workflow/${workflowId}/history`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch transition history');
  },

  /**
   * Get combined history (transitions + activities) for a workflow.
   * @param workflowId The workflow ID
   * @param priority Optional priority filter: 'High', 'Medium', 'Low'
   * @param signal Optional AbortSignal for request cancellation
   */
  getCombinedHistory: async (
    workflowId: number,
    priority?: ActivityPriority,
    signal?: AbortSignal
  ): Promise<WorkflowHistoryItem[]> => {
    const params = priority ? { priority } : undefined;
    const response = await apiService.get<WorkflowHistoryItem[]>(
      `/workflow/${workflowId}/combined-history`,
      params,
      signal
    );
    return unwrapResponse(response, 'Failed to fetch combined history');
  },

  // ==========================================================================
  // BATCH WORKFLOW LIST & FILTERING
  // ==========================================================================

  /**
   * Get paginated list of batch workflows with filtering.
   * @param request Pagination and filter parameters
   * @param signal Optional AbortSignal for request cancellation
   */
  getBatches: async (request: BatchWorkflowListRequest = {}, signal?: AbortSignal): Promise<BatchWorkflowListResponse> => {
    const params = new URLSearchParams();
    const appendIfDefined = (key: string, value: string | number | boolean | undefined | null) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    };

    appendIfDefined('pageNumber', request.pageNumber);
    appendIfDefined('pageSize', request.pageSize);
    appendIfDefined('statusCode', request.statusCode);
    if (request.statusIds && request.statusIds.length > 0) {
      request.statusIds.forEach(id => params.append('statusIds', id.toString()));
    }
    appendIfDefined('priority', request.priority);
    appendIfDefined('isOverdue', request.isOverdue);
    appendIfDefined('includeCompleted', request.includeCompleted);
    appendIfDefined('sortBy', request.sortBy);
    appendIfDefined('sortOrder', request.sortOrder);
    appendIfDefined('searchTerm', request.searchTerm);
    appendIfDefined('sampleType', request.sampleType);
    appendIfDefined('createdAfter', request.createdAfter);
    appendIfDefined('createdBefore', request.createdBefore);
    appendIfDefined('farmerId', request.farmerId);
    appendIfDefined('farmId', request.farmId);
    appendIfDefined('excludeMerged', request.excludeMerged);

    const queryString = params.toString();
    const url = queryString ? `/workflow/batches?${queryString}` : '/workflow/batches';

    const response = await apiService.get<BatchWorkflowListResponse>(url, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch batch workflows');
  },

  /**
   * Export the workflow list to Excel and trigger a browser download.
   * Sends the same filter/search/sort parameters as getBatches; pagination is
   * omitted because the server exports the FULL filtered result set.
   */
  exportBatches: async (request: BatchWorkflowListRequest = {}): Promise<void> => {
    const params = new URLSearchParams();
    const appendIfDefined = (key: string, value: string | number | boolean | undefined | null) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    };

    appendIfDefined('statusCode', request.statusCode);
    appendIfDefined('priority', request.priority);
    appendIfDefined('isOverdue', request.isOverdue);
    appendIfDefined('includeCompleted', request.includeCompleted);
    appendIfDefined('sortBy', request.sortBy);
    appendIfDefined('sortOrder', request.sortOrder);
    appendIfDefined('searchTerm', request.searchTerm);
    appendIfDefined('sampleType', request.sampleType);
    appendIfDefined('createdAfter', request.createdAfter);
    appendIfDefined('createdBefore', request.createdBefore);
    appendIfDefined('farmerId', request.farmerId);
    appendIfDefined('farmId', request.farmId);
    appendIfDefined('excludeMerged', request.excludeMerged);

    const queryString = params.toString();
    const url = queryString ? `/workflow/batches/export?${queryString}` : '/workflow/batches/export';

    const response = await apiClient.get(url, { responseType: 'blob', timeout: 300000 });

    // Prefer the server-provided timestamped filename from Content-Disposition
    let filename = 'DRIS_Workflows.xlsx';
    const contentDisposition = response.headers?.['content-disposition'];
    if (contentDisposition) {
      const utf8Match = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
      const quotedMatch = contentDisposition.match(/filename="(.+?)"/);
      const plainMatch = contentDisposition.match(/filename=([^;\s]+)/);
      const match = utf8Match || quotedMatch || plainMatch;
      if (match) {
        filename = decodeURIComponent(match[1]);
      }
    }

    const objectUrl = window.URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  },

  // ==========================================================================
  // DASHBOARD & METRICS
  // ==========================================================================

  /**
   * Get workflow dashboard metrics.
   * @param signal Optional AbortSignal for request cancellation
   */
  getDashboard: async (signal?: AbortSignal): Promise<WorkflowDashboardData> => {
    const response = await apiService.get<WorkflowDashboardData>('/workflow/dashboard', undefined, signal);
    return unwrapResponse(response, 'Failed to fetch workflow dashboard');
  },

  // ==========================================================================
  // WORKFLOW DOCUMENTS
  // ==========================================================================

  /**
   * Get all documents for a workflow.
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getDocuments: async (workflowId: number, signal?: AbortSignal): Promise<WorkflowDocument[]> => {
    const response = await apiService.get<WorkflowDocument[]>(`/workflow/${workflowId}/documents`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch workflow documents');
  },

  /**
   * Upload a document to a workflow (multipart/form-data).
   * @param workflowId The workflow ID
   * @param file The file to upload
   * @param category Document category (Photo, Lab Report, Other)
   * @param description Optional description
   * @param onUploadProgress Optional callback reporting upload percentage (0-100)
   */
  uploadDocument: async (
    workflowId: number,
    file: File,
    category: string,
    description: string,
    onUploadProgress?: (progress: number) => void,
  ): Promise<WorkflowDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    if (category) formData.append('category', category);
    if (description) formData.append('description', description);

    const response = await apiClient.post<ApiResponse<WorkflowDocument>>(
      `/workflow/${workflowId}/documents`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onUploadProgress
          ? (progressEvent) => {
              const total = progressEvent.total || file.size;
              const progress = Math.round((progressEvent.loaded * 100) / total);
              onUploadProgress(progress);
            }
          : undefined,
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to upload document');
    }
    return response.data.data;
  },

  /**
   * Get all documents associated with this workflow's farmer, grouped by workflow.
   * Powers the farmer-wide Documents view on the review Workspace.
   * @param workflowId The workflow ID being reviewed (resolves the farmer server-side)
   * @param signal Optional AbortSignal for request cancellation
   */
  getRelatedDocuments: async (workflowId: number, signal?: AbortSignal): Promise<RelatedWorkflowDocuments> => {
    const response = await apiService.get<RelatedWorkflowDocuments>(`/workflow/${workflowId}/documents/related`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch related documents');
  },

  /**
   * Get a presigned download URL for a workflow document.
   * @param workflowId The workflow ID
   * @param documentId The document ID
   */
  getDocumentDownloadUrl: async (workflowId: number, documentId: number): Promise<string> => {
    const response = await apiService.get<string>(`/workflow/${workflowId}/documents/${documentId}/download`);
    return unwrapResponse(response, 'Failed to get document download URL');
  },

  /**
   * Delete a workflow document.
   * @param workflowId The workflow ID
   * @param documentId The document ID
   */
  deleteDocument: async (workflowId: number, documentId: number): Promise<boolean> => {
    const response = await apiService.delete<boolean>(`/workflow/${workflowId}/documents/${documentId}`);
    return unwrapResponse(response, 'Failed to delete document');
  },

  // ==========================================================================
  // SAGE DOCUMENT LINKS
  // ==========================================================================

  /**
   * Get all Sage document links for a workflow.
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getSageDocuments: async (workflowId: number, signal?: AbortSignal): Promise<SageDocumentLink[]> => {
    const response = await apiService.get<SageDocumentLink[]>(`/workflow/${workflowId}/sage-documents`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch Sage documents');
  },

  /**
   * Create a new Sage document link for a workflow.
   * @param workflowId The workflow ID
   * @param dto The document link data
   */
  createSageDocumentLink: async (workflowId: number, dto: CreateSageDocumentLinkDto): Promise<SageDocumentLink> => {
    const response = await apiService.post<SageDocumentLink>(`/workflow/${workflowId}/sage-documents`, dto);
    return unwrapResponse(response, 'Failed to create Sage document link');
  },

  /**
   * Update the status of a Sage document link.
   * @param workflowId The workflow ID
   * @param documentId The document link ID
   * @param dto The status update data
   */
  updateSageDocumentStatus: async (workflowId: number, documentId: number, dto: UpdateSageDocumentLinkStatusDto): Promise<SageDocumentLink> => {
    const response = await apiService.put<SageDocumentLink>(`/workflow/${workflowId}/sage-documents/${documentId}/status`, dto);
    return unwrapResponse(response, 'Failed to update Sage document status');
  },

  /**
   * Delete a Sage document link.
   * @param workflowId The workflow ID
   * @param documentId The document link ID
   */
  deleteSageDocumentLink: async (workflowId: number, documentId: number): Promise<boolean> => {
    const response = await apiService.delete<boolean>(`/workflow/${workflowId}/sage-documents/${documentId}`);
    return unwrapResponse(response, 'Failed to delete Sage document link');
  },

  /**
   * Trigger sync for a Sage document link.
   * @param workflowId The workflow ID
   * @param documentId The document link ID
   */
  syncSageDocument: async (workflowId: number, documentId: number): Promise<SageDocumentLink> => {
    const response = await apiService.post<SageDocumentLink>(`/workflow/${workflowId}/sage-documents/${documentId}/sync`);
    return unwrapResponse(response, 'Failed to sync Sage document');
  },

  // ==========================================================================
  // LAB INTAKE
  // ==========================================================================

  /**
   * Get lab intake data (delivery info + sample lines) for a workflow.
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getLabIntake: async (workflowId: number, signal?: AbortSignal): Promise<LabIntake> => {
    const response = await apiService.get<LabIntake>(`/workflow/${workflowId}/lab-intake`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch lab intake data');
  },

  /**
   * Update delivery info for a workflow.
   * @param workflowId The workflow ID
   * @param dto The delivery info data
   */
  updateDeliveryInfo: async (workflowId: number, dto: UpdateDeliveryInfoDto): Promise<LabIntake> => {
    const response = await apiService.put<LabIntake>(`/workflow/${workflowId}/lab-intake/delivery`, dto);
    return unwrapResponse(response, 'Failed to update delivery info');
  },

  /**
   * Save all sample intake lines for a workflow (batch create/update/delete).
   * @param workflowId The workflow ID
   * @param dto The sample intake lines data
   */
  saveSampleIntakeLines: async (workflowId: number, dto: SaveSampleIntakeLinesDto): Promise<LabIntake> => {
    const response = await apiService.put<LabIntake>(`/workflow/${workflowId}/lab-intake/lines`, dto);
    return unwrapResponse(response, 'Failed to save sample intake lines');
  },

  /**
   * Delete a single sample intake line.
   * @param workflowId The workflow ID
   * @param lineId The line ID
   */
  deleteSampleIntakeLine: async (workflowId: number, lineId: number): Promise<boolean> => {
    const response = await apiService.delete<boolean>(`/workflow/${workflowId}/lab-intake/lines/${lineId}`);
    return unwrapResponse(response, 'Failed to delete sample intake line');
  },

  /**
   * Populate sample intake lines from uploaded sample files.
   * Parses files, extracts unique LandRef values, creates intake lines.
   * Replaces all existing lines.
   * @param workflowId The workflow ID
   */
  populateFromFiles: async (workflowId: number): Promise<LabIntake> => {
    const response = await apiService.post<LabIntake>(
      `/workflow/${workflowId}/lab-intake/populate-from-files`
    );
    return unwrapResponse(response, 'Failed to populate lines from files');
  },

  // ==========================================================================
  // SAMPLE FILES (SAP, TISSUE, SOIL)
  // ==========================================================================

  /**
   * Get all sample files for a workflow (SAP, Tissue, Soil).
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getSampleFiles: async (workflowId: number, signal?: AbortSignal): Promise<WorkflowSampleFiles> => {
    const response = await apiService.get<WorkflowSampleFiles>(`/workflow/${workflowId}/sample-files`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch sample files');
  },

  /**
   * Upload or replace a sample file for a workflow.
   * @param workflowId The workflow ID
   * @param fileType The sample file type (sap, tissue, soil)
   * @param file The file to upload
   * @param onUploadProgress Optional callback reporting upload percentage (0-100)
   */
  uploadSampleFile: async (
    workflowId: number,
    fileType: string,
    file: File,
    onUploadProgress?: (progress: number) => void,
  ): Promise<WorkflowSampleFile> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<ApiResponse<WorkflowSampleFile>>(
      `/workflow/${workflowId}/sample-files/${fileType.toLowerCase()}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onUploadProgress
          ? (progressEvent) => {
              const total = progressEvent.total || file.size;
              const progress = Math.round((progressEvent.loaded * 100) / total);
              onUploadProgress(progress);
            }
          : undefined,
      },
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.message || 'Failed to upload sample file');
    }
    return response.data.data;
  },

  /**
   * Get a presigned download URL for a sample file.
   * @param workflowId The workflow ID
   * @param fileType The sample file type (sap, tissue, soil)
   */
  getSampleFileDownloadUrl: async (workflowId: number, fileType: string): Promise<string> => {
    const response = await apiService.get<string>(`/workflow/${workflowId}/sample-files/${fileType.toLowerCase()}/download`);
    return unwrapResponse(response, 'Failed to get sample file download URL');
  },

  /**
   * Delete a sample file from a workflow.
   * @param workflowId The workflow ID
   * @param fileType The sample file type (sap, tissue, soil)
   */
  deleteSampleFile: async (workflowId: number, fileType: string): Promise<boolean> => {
    await apiService.delete<void>(`/workflow/${workflowId}/sample-files/${fileType.toLowerCase()}`);
    return true;
  },

  // ==========================================================================
  // SAMPLE FILE VALIDATION & PROCESSING
  // ==========================================================================

  /**
   * Validate uploaded sample files before processing.
   * Checks sample counts, LandRef matches, duplicates, and other validation rules.
   * @param workflowId The workflow ID
   */
  validateSampleFiles: async (workflowId: number): Promise<WorkflowFileValidationResult> => {
    const response = await apiService.post<WorkflowFileValidationResult>(
      `/workflow/${workflowId}/sample-files/validate`
    );
    const rawResult = unwrapResponse(response, 'Failed to validate sample files');
    return transformValidationResult(rawResult);
  },

  // ==========================================================================
  // REPORT DELIVERY
  // ==========================================================================

  /**
   * Get email preview data for report delivery compose dialog.
   * Returns workflow context (farmer, farm, crop) and suggested compose field values.
   * @param workflowId The workflow ID
   */
  getReportEmailPreview: async (workflowId: number): Promise<ReportEmailPreviewData> => {
    const response = await apiService.get<ReportEmailPreviewData>(`/workflow/${workflowId}/report-email-preview`);
    return unwrapResponse(response, 'Failed to fetch report email preview');
  },

  /**
   * Send reports to recipients for a workflow in AWAITING_SEND status.
   * After successful delivery, the workflow transitions to AWAITING_INVOICE.
   * @param workflowId The workflow ID
   * @param request The report items and recipient emails
   */
  sendReports: async (workflowId: number, request: SendReportsRequest): Promise<SendReportsResult> => {
    const response = await apiService.post<SendReportsResult>(`/workflow/${workflowId}/send-reports`, request);
    return unwrapResponse(response, 'Failed to send reports');
  },

  // ==========================================================================
  // ENTITY SEARCH
  // ==========================================================================

  /**
   * Search across Farmers, Agents, and Labs for entity assignment selectors.
   * Returns a combined, sorted list from a single endpoint.
   */
  searchEntities: async (params: {
    searchTerm?: string;
    entityTypes?: string[];
    pageSize?: number;
  }): Promise<EntitySearchResult[]> => {
    const searchParams = new URLSearchParams();
    if (params.searchTerm) searchParams.set('searchTerm', params.searchTerm);
    if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
    if (params.entityTypes) {
      params.entityTypes.forEach((t) => searchParams.append('entityTypes', t));
    }
    const url = `/workflow/entities/search?${searchParams.toString()}`;
    const response = await apiService.get<EntitySearchResult[]>(url);
    return unwrapResponse(response, 'Failed to search entities');
  },

  // ==========================================================================
  // RESPONSIBLE USERS
  // ==========================================================================

  /**
   * Get the users responsible for transitioning a workflow at its current status.
   * Resolves roles with CanTransition permission and returns the actual users who hold those roles.
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getResponsibleUsers: async (workflowId: number, signal?: AbortSignal): Promise<ResponsibleUser[]> => {
    const response = await apiService.get<ResponsibleUser[]>(
      `/workflow/${workflowId}/responsible-users`,
      undefined,
      signal
    );
    return unwrapResponse(response, 'Failed to fetch responsible users');
  },

  // ==========================================================================
  // REVIEW APPROVALS
  // ==========================================================================

  /**
   * Get review approval state for a workflow.
   * Returns all 3 approval items and whether all are approved.
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getReviewApprovals: async (workflowId: number, signal?: AbortSignal): Promise<WorkflowReviewApprovalState> => {
    const response = await apiService.get<WorkflowReviewApprovalState>(
      `/workflow/${workflowId}/review-approvals`,
      undefined,
      signal
    );
    return unwrapResponse(response, 'Failed to fetch review approvals');
  },

  /**
   * Approve a single review item.
   * When all 3 are approved, the workflow automatically transitions to AWAITING_SEND.
   * @param workflowId The workflow ID
   * @param approvalType The approval type (Interpretation, Recommendation, ReportPdf)
   */
  approveReviewItem: async (
    workflowId: number,
    approvalType: WorkflowApprovalType
  ): Promise<WorkflowReviewApprovalState> => {
    const response = await apiService.post<WorkflowReviewApprovalState>(
      `/workflow/${workflowId}/review-approvals/${encodeURIComponent(approvalType)}/approve`
    );
    return unwrapResponse(response, 'Failed to approve review item');
  },

  /**
   * Revoke approval on a single review item.
   * @param workflowId The workflow ID
   * @param approvalType The approval type (Interpretation, Recommendation, ReportPdf)
   */
  revokeReviewItem: async (
    workflowId: number,
    approvalType: WorkflowApprovalType
  ): Promise<WorkflowReviewApprovalState> => {
    const response = await apiService.post<WorkflowReviewApprovalState>(
      `/workflow/${workflowId}/review-approvals/${encodeURIComponent(approvalType)}/revoke`
    );
    return unwrapResponse(response, 'Failed to revoke review item');
  },

  // ==========================================================================
  // COMMENTS
  // ==========================================================================

  /**
   * Get all comments for a workflow.
   * @param workflowId The workflow ID
   * @param signal Optional AbortSignal for request cancellation
   */
  getComments: async (workflowId: number, signal?: AbortSignal): Promise<WorkflowComment[]> => {
    const response = await apiService.get<WorkflowComment[]>(
      `/workflow/${workflowId}/comments`, undefined, signal);
    return unwrapResponse(response, 'Failed to fetch comments');
  },

  /**
   * Add a comment to a workflow.
   * @param workflowId The workflow ID
   * @param content The comment text
   */
  addComment: async (workflowId: number, content: string): Promise<WorkflowComment> => {
    const response = await apiService.post<WorkflowComment>(
      `/workflow/${workflowId}/comments`, { content });
    return unwrapResponse(response, 'Failed to add comment');
  },

  /**
   * Update an existing workflow comment.
   * @param workflowId The workflow ID
   * @param commentId The comment ID
   * @param content The updated comment text
   */
  updateComment: async (workflowId: number, commentId: number, content: string): Promise<WorkflowComment> => {
    const response = await apiService.put<WorkflowComment>(
      `/workflow/${workflowId}/comments/${commentId}`, { content });
    return unwrapResponse(response, 'Failed to update comment');
  },

  /**
   * Delete a workflow comment.
   * @param workflowId The workflow ID
   * @param commentId The comment ID
   */
  deleteComment: async (workflowId: number, commentId: number): Promise<boolean> => {
    const response = await apiService.delete<boolean>(
      `/workflow/${workflowId}/comments/${commentId}`);
    return unwrapResponse(response, 'Failed to delete comment');
  },

  // ==========================================================================
  // REPORT PDF GENERATION & STATUS
  // ==========================================================================

  /**
   * Generate the Interpretation PDF for a workflow.
   * @param workflowId The workflow ID
   */
  generateInterpretationPdf: async (workflowId: number, batchType?: string): Promise<void> => {
    const params = batchType ? `?batchType=${batchType}` : '';
    const response = await apiService.post<void>(
      `/workflow/${workflowId}/report-pdfs/interpretation/generate${params}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to generate Interpretation PDF');
    }
  },

  /**
   * Generate the Recommendation PDF for a workflow.
   * @param workflowId The workflow ID
   * @param batchType Optional batch type to target
   */
  generateRecommendationPdf: async (workflowId: number, batchType?: string): Promise<void> => {
    const params = batchType ? `?batchType=${batchType}` : '';
    const response = await apiService.post<void>(
      `/workflow/${workflowId}/report-pdfs/recommendation/generate${params}`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to generate Recommendation PDF');
    }
  },

  /**
   * Regenerate the Report PDF for a workflow's batch (queues a background job).
   * @param workflowId The workflow ID
   * @param batchId The batch ID linked to the workflow
   */
  regenerateReportPdf: async (batchId: number): Promise<void> => {
    const response = await apiService.post<unknown>(
      `/batch/${batchId}/regenerate-pdf`
    );
    if (!response.success) {
      throw new Error(response.message || 'Failed to regenerate Report PDF');
    }
  },

  /**
   * Download a report PDF as a blob (proxied through the API).
   * @param workflowId The workflow ID
   * @param type The report type
   */
  downloadReportPdf: async (workflowId: number, type: ReportType, batchType?: string): Promise<Blob> => {
    const path = type === 'ReportPdf' ? 'report' : type.toLowerCase();
    const params = batchType ? `?batchType=${batchType}` : '';
    const response = await apiClient.get(`/workflow/${workflowId}/report-pdfs/${path}/download${params}`, {
      responseType: 'blob',
      timeout: 300000,
    });

    if (response.status !== 200) {
      throw new Error('Failed to download report PDF');
    }

    return response.data as Blob;
  },

  /**
   * Get PDF generation statuses for all report types on a workflow.
   * @param workflowId The workflow ID
   */
  getReportPdfStatuses: async (workflowId: number): Promise<Record<ReportType, BatchTypePdfStatus[]>> => {
    const response = await apiService.get<Record<string, unknown>>(
      `/workflow/${workflowId}/report-pdfs/statuses`
    );
    const data = unwrapResponse(response, 'Failed to get report PDF statuses');

    // Parse new array format from API
    const parse = (key: string, camelKey: string): BatchTypePdfStatus[] => {
      const val = data[key] ?? data[camelKey];
      if (Array.isArray(val)) return val as BatchTypePdfStatus[];
      // Legacy flat format: wrap in array as SapTissue for backward compat
      if (val && typeof val === 'object' && 'exists' in val) {
        return [{ batchType: 'SapTissue' as const, batchId: 0, status: val as ReportPdfStatus }];
      }
      return [];
    };

    return {
      Interpretation: parse('Interpretation', 'interpretation'),
      Recommendation: parse('Recommendation', 'recommendation'),
      ReportPdf: parse('ReportPdf', 'reportPdf'),
    };
  },
};